import React from 'react';

import { isHeroPlannerScheduleDebugFailure } from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleDebug';

import { Chip, Stack, Typography } from '@mui/material';

import { HeroPlannerDebugRow, isTailDealsSchedule } from '../../utils/heroPlannerDebugUtils';

import HeroPlannerDebugEmailSummary from './HeroPlannerDebugEmailSummary';

interface Props {
  row: HeroPlannerDebugRow;
  offerId: string;
}

function HeroPlannerDebugResultDetails({ row, offerId }: Props) {
  const { outcome, heroRow } = row;

  if (row.status === 'skipped') {
    return (
      <Typography variant="body2" color="text.secondary">
        {row.skipReason}
      </Typography>
    );
  }

  if (!outcome || row.status === 'loading') {
    return (
      <Typography variant="body2" color="text.disabled">
        {outcome ? 'No tail deals schedule; looking up the hero planner row…' : 'Waiting for response…'}
      </Typography>
    );
  }

  if (row.status === 'regenerating') {
    const note = (
      <Typography variant="caption" color="warning.main" ml={heroRow ? 1 : 0}>
        Tail deals aren't DONE, so this can't be sent yet. If it stays like this, the generation job likely died -
        regenerate the country group.
      </Typography>
    );

    return heroRow ? (
      <HeroPlannerDebugEmailSummary
        emailStatus={heroRow.emailStatus}
        subjectLine={heroRow.emailSubjectLine}
        heroOfferIds={heroRow.emailHeroDeals ?? []}
        offerId={offerId}
        footer={note}
      />
    ) : (
      note
    );
  }

  if (row.status === 'heroOnly' && heroRow && isHeroPlannerScheduleDebugFailure(outcome)) {
    return (
      <HeroPlannerDebugEmailSummary
        emailStatus={heroRow.emailStatus}
        subjectLine={heroRow.emailSubjectLine}
        heroOfferIds={heroRow.emailHeroDeals ?? []}
        offerId={offerId}
        footer={
          <Typography variant="caption" color="text.secondary" ml={1}>
            No tail deals yet ({outcome.message})
          </Typography>
        }
      />
    );
  }

  if (isHeroPlannerScheduleDebugFailure(outcome)) {
    return (
      <Stack gap={0.5}>
        <Typography variant="body2" color="error.main" fontWeight="medium">
          {outcome.message}
        </Typography>
        {row.heroRowNote && (
          <Typography variant="caption" color="text.secondary">
            {row.heroRowNote}
          </Typography>
        )}
      </Stack>
    );
  }

  const schedule = outcome.ok ? outcome.schedule : undefined;

  if (!isTailDealsSchedule(schedule)) {
    return (
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" variant="outlined" label={`Custom · ${schedule?.type ?? 'unknown'}`} />
        {row.offerSlots.length > 0 && <Chip size="small" color="info" label={`Contains ${offerId}`} />}
      </Stack>
    );
  }

  return (
    <HeroPlannerDebugEmailSummary
      emailStatus={schedule.emailStatus}
      subjectLine={schedule.emailSubjectLine}
      heroOfferIds={schedule.emailHeroDeals}
      offerId={offerId}
      footer={
        <>
          <Typography variant="caption" color="text.secondary" ml={1}>
            +{schedule.emailTailDeals?.length ?? 0} tail deals
          </Typography>
          {row.offerSlots
            .filter((slot) => slot !== 'hero')
            .map((slot) => (
              <Chip key={slot} size="small" color="info" label={`Offer in ${slot}`} />
            ))}
        </>
      }
    />
  );
}

export default HeroPlannerDebugResultDetails;
